import React, { ForwardedRef, forwardRef, useContext, useEffect, useRef } from 'react';
import Container from '../../../../../constants/Container';
import { Grid, Typography } from '@mui/material';
import CheckBoxInput from '../../../../../constants/CheckBoxInput';
import { BillingContext } from '../../../../../context/BillinEntryContext';
import InputTextBox from '../../../../../constants/InputTextBox';
import { Button } from '../../../../../constants/Button';
import { EndBaseLine } from '../../../../../constants/AppSpace';
import LabelBox from '../../../../../constants/LabelBox';
import { PaymentModeOptions, ProductSubLedgerOptions } from '../../../../../constants/Options';
import { mahasulBillingEntrySave } from '../../../../../helpers/submitDataOnServer';
import { useDispatch } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import BillPrintView from './widgets/BillPrintView';
import { useSelector } from 'react-redux';
import { ToastWarning } from '../../../../../constants/Toast';
import { RESET_BILLING_PARTICULAR_LIST } from '../../../../../store/accountSlice';
import SelectionBox from '../../../../../constants/SelectionBox';

interface TransactionFormViewProps {
    data: any;
}
const TransactionFormView: React.FC<TransactionFormViewProps> = ({ data }) => {
    const RenderListComponent = forwardRef<HTMLDivElement, {}>((props, ref: ForwardedRef<HTMLDivElement>) => {
        return (
            <div ref={ref}>
                <BillPrintView />
            </div>
        );
    });

    const billingParticularList = useSelector(
        (state: any) => state.accounts.BillingParticularList
    );
    const dispatch = useDispatch();
    const context = useContext(BillingContext);
    useEffect(() => {
        const refund_advance_amt = values.receive_amt >= values.total_pay_amt ? values.receive_amt - values.total_pay_amt : 0;
        setFieldValue('refund_advance_amt', refund_advance_amt);
        const total_pay_amt = values.check_ad_advace ? values.right_total_amt - values.advance_amt : values.right_total_amt;
        setFieldValue('total_pay_amt', total_pay_amt);
        setFieldValue('advance_amt', data?.total_adv_amt || 0);
    }, [data?.per_month_amt, context?.values.receive_amt, context?.values.advance_amt, context?.values.check_ad_advace, context?.values.right_total_amt]);
    const componentListRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        setFieldValue('fine_amt', data?.total_fine_amt || 0);
    }, [data, data?.members?.member_sn]);
    const handlePrintCard = useReactToPrint({
        content: () => componentListRef.current!,
    });

    if (!context) {
        return <Typography>Error: Context not available</Typography>;
    }
    const { values, ResetForm, setFieldValue, handleChange, handleBlur, touched, errors } = context;
    const paymentModeOptions = PaymentModeOptions(data?.payment_mode);
    const subLedgerOptions = ProductSubLedgerOptions(data?.sub_ledger);
    // inside TransactionFormView

    const trans_form_save = async () => {
        if (values.total_pay_amt <= values.receive_amt) {
            await mahasulBillingEntrySave(values, dispatch, billingParticularList); // pass data
            await ResetForm();
            setFieldValue('check_ad_advace', false);
            dispatch(RESET_BILLING_PARTICULAR_LIST());
            setFieldValue("refund_advance_amt", 0);
        } else {
            ToastWarning({ message: "Check your receivable amount" });
        }
    };

    return (
        <Container padding={true}>
            <Grid container rowSpacing={1} columnSpacing={3}>
                <Grid item xl={6} lg={6} md={6} xs={12} sm={12}>
                    <Grid container spacing={1}>
                        <Grid item xl={12} lg={12} md={12} xs={12} sm={12}>
                            <CheckBoxInput
                                label={'Adjust Advance'}
                                name="check_ad_advace"
                                handleChange={handleChange}
                                value={values.check_ad_advace || false}
                            />
                        </Grid>
                        <Grid item xl={12} lg={12} md={12} xs={12} sm={12}>
                            <InputTextBox
                                type='number'
                                label='Advance Balance'
                                disabled
                                name='advance_amt'
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                value={values.advance_amt}
                                placeholder='0.00'
                                error={errors.advance_amt}
                                required={true}
                            />
                        </Grid>

                        <Grid item xl={12} lg={12} md={12} xs={12} sm={12}>
                            <SelectionBox
                                label='Payment Mode'
                                options={paymentModeOptions}
                                handleChange={(name, selectedOption: any) => {
                                    setFieldValue(name, selectedOption.value)
                                }}
                                required={true}
                                placeholder='Select Payment Mode'
                                name='payment_mode'
                                handleBlur={handleBlur}
                                value={values.payment_mode}
                                touched={touched.payment_mode}
                                errors={errors.payment_mode}
                            />
                        </Grid>
                        {
                            values.payment_mode?.toString() !== "5" && <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                                <SelectionBox
                                    label='Select Bank'
                                    options={subLedgerOptions}
                                    name='bank_sglsn'
                                    required={values.payment_mode?.toString() === "5" ? false : true}
                                    placeholder='Select Bank'
                                    handleChange={(name, selectedOption: any) => {
                                        setFieldValue(name, selectedOption.value)
                                    }}
                                    handleBlur={handleBlur}
                                    value={values.bank_sglsn}
                                    touched={touched.bank_sglsn}
                                    errors={errors.bank_sglsn}
                                />
                            </Grid>
                        }
                    </Grid>
                </Grid>

                <Grid item xl={6} lg={6} md={6} xs={12} sm={12}>
                    <Grid container spacing={1}>
                        <Grid item xl={12} lg={12} md={12} xs={12} sm={12}>
                            <InputTextBox
                                type='number'
                                label='Receive Amount'
                                name='receive_amt'
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                value={values.receive_amt}
                                placeholder='0.00'
                                error={errors.receive_amt}
                                required={true}
                            />
                        </Grid>
                        <Grid item xl={12} lg={12} md={12} xs={12} sm={12}>
                            <InputTextBox
                                type='number'
                                label='Refound Amount'
                                disabled
                                name='refund_advance_amt'
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                value={values.receive_amt > values.total_pay_amt ? values.refund_advance_amt : 0}
                                placeholder='0.00'
                                error={errors.refund_advance_amt}
                                required={true}
                            />
                        </Grid>
                        <Grid item xl={12} lg={12} md={12} xs={12} sm={12}>
                            <CheckBoxInput
                                handleChange={handleChange}
                                label="Adjust Advance"
                                disabled={values.refund_advance_amt < 1}
                                name="adjust_advance"
                                value={values.adjust_advance}
                            />
                        </Grid>
                        <Grid item xl={12} lg={12} md={12} xs={12} sm={12}>
                            <LabelBox label='Net Amount' value={values.right_total_amt} />
                        </Grid>
                        <Grid item xl={12} lg={12} md={12} xs={12} sm={12}>
                            <LabelBox label='Adjust Advance' value={values.check_ad_advace ? values.advance_amt : 0} />
                        </Grid>
                        <Grid item xl={12} lg={12} md={12} xs={12} sm={12}>
                            <LabelBox label='Paid Total Amount' value={values.total_pay_amt} />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xl={12} lg={12} md={12} xs={12} sm={12}>
                    <EndBaseLine top={14}>
                        <Button btn_type='print' onClick={handlePrintCard} type='button' el='button' />
                        <Button btn_type='save' onClick={trans_form_save} type='button' el='button' />
                    </EndBaseLine>
                </Grid>
            </Grid>
            <div className='hidden'>
                <div ref={componentListRef}>
                    <RenderListComponent />
                </div>
            </div>
        </Container>
    )
}

export default TransactionFormView